import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import {
  Search, Bell, MessageSquare, Menu, X, User, Settings,
  LogOut, PlusCircle, LayoutDashboard, Heart, ChevronDown
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';

export default function Navbar() {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (user) {
      api.get('/notifications').then(res => setUnread(res.data.unread || 0)).catch(() => {});
    }
  }, [user]);

  const handleLogout = () => {
    logout();
    toast.success('تم تسجيل الخروج بنجاح');
    navigate('/');
    setUserMenuOpen(false);
  };

  return (
    <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${
      scrolled ? 'bg-dark-900/95 backdrop-blur-md shadow-lg shadow-black/20 border-b border-dark-700' : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 bg-gradient-to-br from-primary-500 to-primary-700 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm font-display">SP</span>
            </div>
            <span className="text-xl font-bold gradient-text tracking-wide">SPORTIX</span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            <Link to="/listings?type=venue" className="text-dark-300 hover:text-white text-sm transition-colors">الملاعب</Link>
            <Link to="/listings?type=equipment" className="text-dark-300 hover:text-white text-sm transition-colors">المعدات</Link>
            <Link to="/listings?type=service" className="text-dark-300 hover:text-white text-sm transition-colors">الخدمات</Link>
            <Link to="/search" className="text-dark-300 hover:text-white text-sm transition-colors flex items-center gap-1">
              <Search size={14} /> بحث
            </Link>
          </div>

          {/* Right Section */}
          <div className="flex items-center gap-3">
            {user ? (
              <>
                {/* Add listing CTA */}
                {(user.role === 'owner' || user.role === 'both' || user.role === 'admin') && (
                  <Link to="/dashboard/add-listing"
                    className="hidden md:flex items-center gap-1.5 bg-primary-600 hover:bg-primary-500 text-white text-sm px-3 py-1.5 rounded-lg transition-colors font-semibold">
                    <PlusCircle size={15} />
                    أضف إعلان
                  </Link>
                )}

                {/* Notifications */}
                <Link to="/dashboard" className="relative p-2 text-dark-300 hover:text-white rounded-lg hover:bg-dark-700 transition-colors">
                  <Bell size={20} />
                  {unread > 0 && (
                    <span className="absolute top-0.5 right-0.5 w-4 h-4 bg-red-500 rounded-full text-xs flex items-center justify-center text-white">
                      {unread > 9 ? '9+' : unread}
                    </span>
                  )}
                </Link>

                {/* Messages */}
                <Link to="/dashboard/messages" className="p-2 text-dark-300 hover:text-white rounded-lg hover:bg-dark-700 transition-colors">
                  <MessageSquare size={20} />
                </Link>

                {/* User menu */}
                <div className="relative">
                  <button onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="flex items-center gap-2 p-1.5 rounded-xl hover:bg-dark-700 transition-colors">
                    {user.avatar ? (
                      <img src={`${process.env.REACT_APP_API_URL?.replace('/api', '')}${user.avatar}`}
                        alt={user.full_name} className="w-8 h-8 rounded-full object-cover" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-primary-600 flex items-center justify-center text-white font-bold text-sm">
                        {user.full_name?.charAt(0)}
                      </div>
                    )}
                    <ChevronDown size={14} className="text-dark-400 hidden md:block" />
                  </button>

                  {userMenuOpen && (
                    <div className="absolute left-0 top-full mt-2 w-52 bg-dark-800 border border-dark-600 rounded-2xl shadow-2xl shadow-black/50 overflow-hidden z-50">
                      <div className="p-3 border-b border-dark-700">
                        <p className="text-white font-semibold text-sm">{user.full_name}</p>
                        <p className="text-dark-400 text-xs truncate">{user.email}</p>
                      </div>
                      <div className="p-2">
                        {[
                          { to: '/dashboard', icon: LayoutDashboard, label: 'لوحة التحكم' },
                          { to: '/dashboard/my-listings', icon: PlusCircle, label: 'إعلاناتي' },
                          { to: '/dashboard/favorites', icon: Heart, label: 'المفضلة' },
                          { to: '/dashboard/profile', icon: Settings, label: 'الإعدادات' },
                          ...(user.role === 'admin' ? [{ to: '/admin', icon: Settings, label: 'إدارة المنصة' }] : [])
                        ].map(({ to, icon: Icon, label }) => (
                          <Link key={to} to={to} onClick={() => setUserMenuOpen(false)}
                            className="flex items-center gap-2.5 px-3 py-2 text-dark-200 hover:text-white hover:bg-dark-700 rounded-xl transition-colors text-sm">
                            <Icon size={15} />{label}
                          </Link>
                        ))}
                        <button onClick={handleLogout}
                          className="w-full flex items-center gap-2.5 px-3 py-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-xl transition-colors text-sm mt-1">
                          <LogOut size={15} />تسجيل الخروج
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/login" className="text-dark-300 hover:text-white text-sm transition-colors px-3 py-1.5">دخول</Link>
                <Link to="/register" className="bg-primary-600 hover:bg-primary-500 text-white text-sm px-4 py-1.5 rounded-lg transition-colors font-semibold">
                  تسجيل
                </Link>
              </div>
            )}

            {/* Mobile menu toggle */}
            <button className="md:hidden p-2 text-dark-300 hover:text-white" onClick={() => setMobileOpen(!mobileOpen)}>
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden border-t border-dark-700 py-3 space-y-1 animate-fade-in">
            {[
              { to: '/listings?type=venue', label: 'الملاعب والمنشآت' },
              { to: '/listings?type=equipment', label: 'المعدات والأجهزة' },
              { to: '/listings?type=service', label: 'الخدمات الرياضية' },
              { to: '/search', label: 'بحث' },
            ].map(({ to, label }) => (
              <Link key={to} to={to} onClick={() => setMobileOpen(false)}
                className="block px-4 py-2 text-dark-300 hover:text-white hover:bg-dark-700 rounded-lg transition-colors text-sm">
                {label}
              </Link>
            ))}
            {user && (user.role === 'owner' || user.role === 'both') && (
              <Link to="/dashboard/add-listing" onClick={() => setMobileOpen(false)}
                className="block px-4 py-2 text-primary-400 hover:text-primary-300 text-sm font-semibold">
                + أضف إعلان جديد
              </Link>
            )}
          </div>
        )}
      </div>

      {/* Overlay */}
      {userMenuOpen && <div className="fixed inset-0 z-40" onClick={() => setUserMenuOpen(false)} />}
    </nav>
  );
}
